import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CryptoService, UserInfoModel } from 'src/app/core/api/crypto-js/crypto.service';
import { EMBEDDED_LANGUAGE_TXT } from './hospital-language.embedded';
import { normalizeHospitalType } from './hospital-type.util';

/**
 * `assets/language/language.txt` は参照用・編集しない。実行時に読み取り、行ごとに
 * `Map<病院側の語, 施設側の語>` を構築する（患者→利用者、看護師→スタッフなど複数ペア）。
 * `t('患者')` と `t('看護師')` は別キーで別結果になる。複合語はテンプレートで分解し、
 * 変わる語だけ `hospLang` / `t()` に渡す。
 * hospitalType は /login レスポンスを UserInfo に保存した値を参照。
 */

@Injectable({ providedIn: 'root' })
export class HospitalLanguageService {
  /** language.txt 左列 → 右列（FACILITY 時のみ右列を返す） */
  private readonly hospitalToFacility = new Map<string, string>();
  private loadPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private crypto: CryptoService,
  ) {}

  /** APP_INITIALIZER 用：埋め込み対照を先に適用し、取得できたら assets の language.txt で上書き */
  load(): Promise<void> {
    if (!this.loadPromise) {
      this.loadPromise = firstValueFrom(
        this.http.get('assets/language/language.txt', { responseType: 'text' }),
      )
        .then((text) => this.buildMapFromText(text))
        .catch(() => this.buildMapFromText(''));
    }
    return this.loadPromise;
  }

  private stripBom(text: string): string {
    return text.replace(/^\uFEFF/, '');
  }

  /** 先に埋め込み全文で Map を構築し、HTTP 本文がある場合は上書き（参照ファイル優先） */
  private buildMapFromText(httpText: string): void {
    this.hospitalToFacility.clear();
    this.mergeLinesIntoMap(EMBEDDED_LANGUAGE_TXT);
    const trimmed = this.stripBom(httpText).trim();
    if (trimmed) {
      this.mergeLinesIntoMap(trimmed);
    }
  }

  private mergeLinesIntoMap(text: string): void {
    for (const rawLine of text.split(/\r?\n/)) {
      const line = rawLine.trim();
      if (!line || !line.includes('↔')) {
        continue;
      }
      const parts = line.split(/\s*↔\s*/);
      if (parts.length < 2) {
        continue;
      }
      const left = parts[0].trim();
      const right = parts[1].trim();
      if (left && right) {
        this.hospitalToFacility.set(left, right);
      }
    }
  }

  getHospitalType(): 'HOSPITAL' | 'FACILITY' {
    const info = this.crypto.get('_s') as UserInfoModel;
    return normalizeHospitalType(info?.hospitalType);
  }

  /**
   * 病院側の語をキーに変換する（`language.txt` 左列）。キーごとに対応する右列が異なる。
   */
  t(hospitalSide: string): string {
    if (!hospitalSide) {
      return hospitalSide;
    }
    if (this.getHospitalType() === 'FACILITY') {
      return this.hospitalToFacility.get(hospitalSide) ?? hospitalSide;
    }
    return hospitalSide;
  }
}
